import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Church as ChurchIcon,
  Download as DownloadIcon,
  Videocam as VideocamIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../../theme";
import { useNavigate } from "react-router-dom";
import patterbackground from "./pattern.jpeg";

interface ResourceLink {
  id: string;
  title: string;
  description: string;
  icon: "church" | "download" | "videocam";
  url: string;
  action?: () => void;
}

interface ChurchThankYouModalProps {
  open: boolean;
  onClose: () => void;
  resources?: ResourceLink[];
  showCheckIcon?: boolean;
  referenceNumber?: string;
  onCompleteDetails?: () => void;
}

const ChurchThankYouModal: React.FC<ChurchThankYouModalProps> = ({
  open,
  onClose,
  showCheckIcon = true,
  referenceNumber,
  onCompleteDetails,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const resources: ResourceLink[] = [
    {
      id: "complete-details",
      title: "Complete Church Details",
      description: "Add additional information for better service",
      icon: "church",
      url: "#",
      action: onCompleteDetails,
    },
    {
      id: "streaming-guide",
      title: "Download Free Livestreaming Guide",
      description: "Complete guide to church livestreaming setup",
      icon: "download",
      url: "", 
    },
    {
      id: "pixel-pilot",
      title: "Learn About Pixel Pilot Program",
      description: "A platform for streaming and production jobs",
      icon: "videocam",
      url: "", 
    },
  ];

  const handleResourceClick = (resource: ResourceLink) => {
    if (resource.action) {
      resource.action();
      onClose();
    } else if (resource.url && resource.url !== "#") {
      if (resource.url.startsWith("http")) {
        window.open(resource.url, "_blank");
      } else {
        navigate(resource.url);
        onClose();
      }
    }
  };

  const getIcon = (iconType: "church" | "download" | "videocam") => {
    switch (iconType) {
      case "church":
        return ChurchIcon;
      case "download":
        return DownloadIcon;
      case "videocam":
        return VideocamIcon;
      default:
        return ChurchIcon;
    }
  };

  const padding = isMobile ? 2 : 2;
  const iconSize = isMobile ? 48 : 60;
  const titleSize = isMobile ? 20 : 24;
  const buttonPadding = isMobile ? 1.5 : 2;
  const gap = isMobile ? 1.5 : 2;
  const mb = isMobile ? 2 : 2;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          margin: isMobile ? 1 : 2,
          maxHeight: isMobile ? "95vh" : "90vh",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          p: 3,
          bgcolor: "#614338",
          color: "white",
          overflow: "hidden",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${patterbackground})`,
            backgroundRepeat: "repeat",
            backgroundSize: "contain",
            opacity: 0.1,
            zIndex: 0,
          }}
        />
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: isMobile ? 6 : 8,
            top: isMobile ? 6 : 8,
            color: "white",
            padding: "4px",
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        {showCheckIcon && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: isMobile ? 1.5 : 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: iconSize, color: "white" }} />
          </Box>
        )}

        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mb: 1, fontSize: titleSize, position: "relative", zIndex: 1 }}
        >
          Application Submitted Successifully!
        </Typography>
        <Typography
          variant={isMobile ? "subtitle1" : "body1"}
          fontWeight="bold"
          sx={{
            fontSize: "1.125rem",
            lineHeight: 1.4,
            color: "white",
            position: "relative",
            zIndex: 1,
          }}
        >
          Your free stream application has been received
        </Typography>

        {referenceNumber && (
          <Typography
            variant="body1"
            sx={{
              mt: 1.5,
              opacity: 0.9,
              fontSize: "0.9375rem",
              lineHeight: 1.4,
              maxWidth: "480px",
              mx: "auto",
              color: colors.primary[500],
              position: "relative",
              zIndex: 1,
            }}
          >
            Reference Number: <strong>{referenceNumber}</strong>
          </Typography>
        )}
        
      </Box>

      <DialogContent sx={{ p: padding }}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{
            mb,
            textAlign: "center",
            fontSize: isMobile ? "0.9375rem" : undefined,
          }}
        >
          Next Steps
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap, mb }}>
          {resources.map((resource) => {
            const Icon = getIcon(resource.icon);
            const isDisabled = !resource.url && !resource.action;

            return (
              <Button
                key={resource.id}
                onClick={() => !isDisabled && handleResourceClick(resource)}
                disabled={isDisabled}
                sx={{
                  background: isDisabled
                    ? "rgba(255,255,255,0.1)"
                    : colors.primary[400],
                  color: isDisabled ? "rgba(255,255,255,0.5)" : "white",
                  border: isDisabled ? "1px solid gray" : "none",
                  p: buttonPadding,
                  borderRadius: 2,
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textAlign: "left",
                  minHeight: isMobile ? 60 : 72,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  "&:hover": {
                    background: isDisabled
                      ? "rgba(255,255,255,0.1)"
                      : "#997466",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: isMobile ? 1.5 : 2,
                  }}
                >
                  <Box
                    sx={{
                      width: isMobile ? 40 : 48,
                      height: isMobile ? 40 : 48,
                      bgcolor: "rgba(255,255,255,0.2)",
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  >
                    <Icon sx={{ fontSize: isMobile ? 20 : 24 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: isMobile ? "0.875rem" : "1rem",
                        mb: isMobile ? 0.25 : 0.5,
                        opacity: isDisabled ? 0.5 : 1,
                      }}
                    >
                      {resource.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.75rem" : "0.875rem",
                        opacity: isDisabled ? 0.5 : 0.9,
                      }}
                    >
                      {resource.description}
                    </Typography>
                  </Box>
                </Box>
                <ArrowForwardIcon
                  sx={{
                    fontSize: isMobile ? 18 : 20,
                    flexShrink: 0,
                    opacity: isDisabled ? 0.5 : 1,
                  }}
                />
              </Button>
            );
          })}
        </Box>

        <Box
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            p: isMobile ? 2 : 2.5,
            mb: isMobile ? 2 : 3,
            textAlign: "center",
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              lineHeight: 1.6,
              fontSize: isMobile ? "0.8125rem" : "0.875rem",
            }}
          >
            Thank you for choosing Muzukuru for your church livestreaming needs.
            Our team will review your application and contact you via WhatsApp
            to confirm your free stream booking.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChurchThankYouModal;
